import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Typography,
  Button,
  Progress,
  Modal,
  DatePicker,
  Tooltip,
} from "antd";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import {
  QuestionCircleOutlined,
  ReloadOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  ArrowRightOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import ShiftEndAlert from "./components/DayEndAlert";

const { Text } = Typography;
const { confirm } = Modal;

const safeNumber = (value) => Number.parseFloat(value || 0) || 0;

const formatMoney = (currency, value) => {
  const amount = safeNumber(value);

  return `${currency}${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const getStorageBusinessDate = () => {
  const raw =
    localStorage.getItem("posbusiness_date") ||
    localStorage.getItem("business_date") ||
    moment().format("YYYY-MM-DD");

  const parsed = moment(raw);

  return parsed.isValid() ? parsed : moment();
};

const savePosBusinessDate = (dateValue) => {
  const parsed = moment(dateValue);
  const formatted = parsed.isValid()
    ? parsed.format("YYYY-MM-DD")
    : moment().format("YYYY-MM-DD");

  localStorage.setItem("posbusiness_date", formatted);
  localStorage.removeItem("business_date");

  return formatted;
};

const clearPosSessionCaches = () => {
  [
    "selectedOrders",
    "billingSummary",
    "searchState",
    "searchParams",
    "filteredOrders",
  ].forEach((key) => localStorage.removeItem(key));
};

const ShiftEndDashboard = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [apiLoading, setApiLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(getStorageBusinessDate);
  const [dashboardData, setDashboardData] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL;
  const currency = localStorage.getItem("currencycode") || "₹";
  const navigate = useNavigate();

  const selectedDateKey = selectedDate.format("YYYY-MM-DD");

  const fetchDashboardData = useCallback(async () => {
    setApiLoading(true);

    const token = localStorage.getItem("token");

    try {
      const response = await axios.get(
        `${apiUrl}/nightauditpos/night-audit-summary-due`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { business_date: selectedDateKey },
        }
      );

      if (response.data?.success) {
        setDashboardData(response.data.summary || {});
      } else {
        throw new Error("Failed to fetch dashboard data");
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Failed to load dashboard data. Please try again.");

      setDashboardData({
        pending_kots: 0,
        unsettled_bills: 0,
        no_show_reservations: 0,
        counter_sales: "0.00",
        room_credit: "0.00",
        cash_collection: "0.00",
        upi_collection: "0.00",
        expenses: "0.00",
      });
    } finally {
      setApiLoading(false);
    }
  }, [apiUrl, selectedDateKey]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleDateChange = (date) => {
    if (!date) return;

    const formattedDate = savePosBusinessDate(date);
    setSelectedDate(moment(formattedDate));
  };

  const handleNavigation = (type) => {
    switch (type) {
      case "pending_kots":
        navigate("/pos/pending-kots");
        break;
      case "unsettled_bills":
        navigate("/pos/unsettled-bills");
        break;
      case "no_show_reservations":
        navigate("/pos/no-show-reservations");
        break;
      default:
        console.warn("Invalid navigation type");
    }
  };

  const handleRunShiftEnd = () => {
    const hasPendingItems =
      (dashboardData?.pending_kots || 0) > 0 ||
      (dashboardData?.unsettled_bills || 0) > 0 ||
      (dashboardData?.no_show_reservations || 0) > 0;

    if (!hasPendingItems) {
      handleShiftEndProcess();
      return;
    }

    confirm({
      title: "Review pending items before day end",
      icon: <QuestionCircleOutlined />,
      content: (
        <div className="ns-dayend-confirm">
          <Text strong>These items are still open:</Text>
          <ul>
            {dashboardData?.pending_kots > 0 && (
              <li>{dashboardData.pending_kots} pending KOTs</li>
            )}
            {dashboardData?.unsettled_bills > 0 && (
              <li>{dashboardData.unsettled_bills} unsettled bills</li>
            )}
            {dashboardData?.no_show_reservations > 0 && (
              <li>{dashboardData.no_show_reservations} no-show reservations</li>
            )}
          </ul>
          <Text type="secondary">
            Review is recommended before closing the POS business day.
          </Text>
        </div>
      ),
      okText: "Proceed Anyway",
      cancelText: "Review First",
      onOk() {
        handleShiftEndProcess();
      },
    });
  };

  const handleShiftEndProcess = async () => {
    const token = localStorage.getItem("token");

    Swal.fire({
      title: "Process day end?",
      text: "This will close the current POS business date and move the outlet to the next business date.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#16a34a",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, process day end",
      cancelButtonText: "Cancel",
      customClass: {
        popup: "ns-dayend-swal",
      },
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      setIsLoading(true);

      try {
        const response = await axios.post(
          `${apiUrl}/nightauditpos/run-night-audit`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.status === 200) {
          const nextBusinessDate = savePosBusinessDate(response.data.newDate);
          const displayDate = moment(nextBusinessDate).format("dddd, DD MMMM YYYY");

          clearPosSessionCaches();

          Swal.fire({
            title: "Day end processed",
            text: `New POS business date is ${displayDate}.`,
            icon: "success",
            confirmButtonText: "Continue",
            confirmButtonColor: "#16a34a",
            customClass: {
              popup: "ns-dayend-swal",
            },
          }).then(() => {
            window.location.href = "/pos";
          });
        } else {
          throw new Error("Unexpected response from server.");
        }
      } catch (error) {
        setIsLoading(false);

        if (error.response?.status === 400) {
          const errorMessage = error.response.data?.message || "An error occurred.";
          const actionRequired = error.response.data?.actionRequired || "";

          Swal.fire({
            title: "Day end blocked",
            html: `<strong>${errorMessage}</strong><br><br><em>${actionRequired}</em>`,
            icon: "warning",
            confirmButtonText: "OK",
            confirmButtonColor: "#2563eb",
            customClass: {
              popup: "ns-dayend-swal",
            },
          });
        } else {
          Swal.fire({
            title: "Unable to process day end",
            text: "Failed to process day end. Please try again later.",
            icon: "error",
            confirmButtonText: "OK",
            confirmButtonColor: "#dc2626",
            customClass: {
              popup: "ns-dayend-swal",
            },
          });
        }
      } finally {
        setIsLoading(false);
      }
    });
  };

  const totals = useMemo(() => {
    const counterSales = safeNumber(dashboardData?.counter_sales);
    const roomCredit = safeNumber(dashboardData?.room_credit);
    const cashCollection = safeNumber(dashboardData?.cash_collection);
    const upiCollection = safeNumber(dashboardData?.upi_collection);
    const expenses = safeNumber(dashboardData?.expenses);

    return {
      counterSales,
      roomCredit,
      cashCollection,
      upiCollection,
      expenses,
      totalSales: counterSales + roomCredit,
      netCashPosition: cashCollection + upiCollection - expenses,
    };
  }, [dashboardData]);

  const pendingCards = useMemo(
    () => [
      {
        key: "pending_kots",
        label: "Pending KOTs",
        description: "Kitchen orders still pending review.",
        value: dashboardData?.pending_kots || 0,
        icon: "fa-ticket-alt",
        tone: "#2563eb",
      },
      {
        key: "unsettled_bills",
        label: "Unsettled Bills",
        description: "Bills not yet settled or closed.",
        value: dashboardData?.unsettled_bills || 0,
        icon: "fa-money-bill-wave",
        tone: "#f59e0b",
      },
      {
        key: "no_show_reservations",
        label: "No-show Reservations",
        description: "Reservations requiring no-show action.",
        value: dashboardData?.no_show_reservations || 0,
        icon: "fa-user-times",
        tone: "#ef4444",
      },
    ],
    [dashboardData]
  );

  const issueCount = pendingCards.filter((item) => item.value > 0).length;
  const pendingItemCount = pendingCards.reduce((sum, item) => sum + safeNumber(item.value), 0);
  const readinessPercent = Math.max(
    0,
    Math.round((1 - (issueCount / pendingCards.length) * 0.33) * 100)
  );
  const isFullyReady = issueCount === 0;
  const loading = apiLoading || isLoading;

  const financialCards = [
    {
      label: "Total Sales",
      value: totals.totalSales,
      icon: "fa-chart-line",
      tone: "#16a34a",
      emphasis: true,
    },
    {
      label: "Counter Sales",
      value: totals.counterSales,
      icon: "fa-cash-register",
      tone: "#2563eb",
    },
    {
      label: "Room Credit",
      value: totals.roomCredit,
      icon: "fa-hotel",
      tone: "#f59e0b",
    },
    {
      label: "Expenses",
      value: totals.expenses,
      icon: "fa-receipt",
      tone: "#64748b",
    },
    {
      label: "Cash Collection",
      value: totals.cashCollection,
      icon: "fa-wallet",
      tone: "#16a34a",
    },
    {
      label: "UPI Collection",
      value: totals.upiCollection,
      icon: "fa-mobile-alt",
      tone: "#06b6d4",
    },
    {
      label: "Net Cash Position",
      value: totals.netCashPosition,
      icon: "fa-balance-scale",
      tone: totals.netCashPosition >= 0 ? "#16a34a" : "#dc2626",
      emphasis: true,
    },
  ];

  const dashboardStyles = `
    .ns-dayend-page-wrapper {
      background:
        radial-gradient(circle at 8% 0%, rgba(37, 99, 235, 0.07), transparent 30%),
        radial-gradient(circle at 92% 2%, rgba(34, 197, 94, 0.08), transparent 28%),
        #f8fafc;
      min-height: calc(100vh - 60px);
    }

    .ns-dayend-page {
      padding-top: 12px !important;
      padding-bottom: 24px;
      color: #0f172a;
    }

    .ns-dayend-loader {
      min-height: calc(100vh - 120px);
      display: flex;
      align-items: center;
      justify-content: center;
      color: #64748b;
    }

    .ns-dayend-loader-card {
      width: min(440px, 92vw);
      background: #ffffff;
      border: 1px solid rgba(15, 23, 42, 0.08);
      border-radius: 24px;
      box-shadow: 0 18px 42px rgba(15, 23, 42, 0.08);
      padding: 28px;
      text-align: center;
    }

    .ns-dayend-dots {
      display: flex;
      gap: 8px;
      justify-content: center;
      margin-bottom: 14px;
    }

    .ns-dayend-dot {
      width: 10px;
      height: 10px;
      background: #2563eb;
      border-radius: 50%;
      animation: ns-dayend-bounce 0.58s infinite alternate;
    }

    .ns-dayend-dot:nth-child(2) { animation-delay: 0.1s; }
    .ns-dayend-dot:nth-child(3) { animation-delay: 0.2s; }
    .ns-dayend-dot:nth-child(4) { animation-delay: 0.3s; }
    .ns-dayend-dot:nth-child(5) { animation-delay: 0.4s; }

    @keyframes ns-dayend-bounce {
      to { transform: translateY(-10px); }
    }

    .ns-dayend-hero {
      background:
        radial-gradient(circle at top left, rgba(37, 99, 235, 0.14), transparent 34%),
        radial-gradient(circle at top right, rgba(34, 197, 94, 0.10), transparent 32%),
        linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
      border: 1px solid rgba(15, 23, 42, 0.08);
      border-radius: 24px;
      box-shadow: 0 16px 42px rgba(15, 23, 42, 0.075);
      padding: 17px 18px;
      margin-bottom: 12px;
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
      flex-wrap: wrap;
    }

    .ns-dayend-hero-left {
      display: flex;
      align-items: flex-start;
      gap: 13px;
      min-width: 0;
    }

    .ns-dayend-hero-icon {
      width: 52px;
      height: 52px;
      border-radius: 19px;
      background: linear-gradient(135deg, #1d4ed8, #2563eb);
      color: #ffffff;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 16px 34px rgba(37, 99, 235, 0.22);
      flex: 0 0 auto;
      font-size: 1.32rem;
    }

    .ns-dayend-eyebrow {
      color: #2563eb;
      font-size: 0.72rem;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      font-weight: 950;
      margin-bottom: 5px;
    }

    .ns-dayend-hero h2 {
      color: #001f5b;
      font-size: 1.42rem;
      line-height: 1.16;
      font-weight: 950;
      margin: 0;
    }

    .ns-dayend-hero p {
      color: #64748b;
      font-size: 0.82rem;
      line-height: 1.5;
      font-weight: 760;
      margin: 7px 0 0;
      max-width: 820px;
    }

    .ns-dayend-hero-actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 8px;
      flex-wrap: wrap;
    }

    .ns-dayend-date-picker {
      min-width: 188px;
    }

    .ns-dayend-date-picker .ant-picker {
      min-height: 42px;
      border-radius: 14px;
      border-color: rgba(15, 23, 42, 0.10);
      font-weight: 900;
    }

    .ns-dayend-btn {
      min-height: 42px !important;
      border-radius: 14px !important;
      font-weight: 950 !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      gap: 8px !important;
      padding: 0 16px !important;
    }

    .ns-dayend-primary {
      background: linear-gradient(135deg, #16a34a, #22c55e) !important;
      border-color: #16a34a !important;
      color: #ffffff !important;
      box-shadow: 0 14px 30px rgba(22, 163, 74, 0.20) !important;
    }

    .ns-dayend-soft {
      background: #ffffff !important;
      color: #334155 !important;
      border: 1px solid rgba(15, 23, 42, 0.10) !important;
    }

    .ns-dayend-status-strip {
      display: grid;
      grid-template-columns: minmax(260px, 0.75fr) minmax(0, 1.25fr);
      gap: 12px;
      margin-bottom: 12px;
    }

    .ns-dayend-readiness-card,
    .ns-dayend-alert-card,
    .ns-dayend-panel,
    .ns-dayend-fin-card {
      background: #ffffff;
      border: 1px solid rgba(15, 23, 42, 0.08);
      border-radius: 24px;
      box-shadow: 0 16px 42px rgba(15, 23, 42, 0.07);
      overflow: hidden;
    }

    .ns-dayend-readiness-card {
      padding: 17px;
      display: grid;
      grid-template-columns: 116px minmax(0, 1fr);
      gap: 15px;
      align-items: center;
    }

    .ns-dayend-readiness-copy span {
      display: block;
      color: #64748b;
      font-size: 0.7rem;
      font-weight: 950;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      margin-bottom: 5px;
    }

    .ns-dayend-readiness-copy strong {
      color: #0f172a;
      display: block;
      font-size: 1.05rem;
      font-weight: 950;
      line-height: 1.22;
    }

    .ns-dayend-readiness-copy p {
      color: #64748b;
      font-size: 0.78rem;
      line-height: 1.4;
      font-weight: 760;
      margin: 6px 0 0;
    }

    .ns-dayend-alert-card {
      padding: 14px 16px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      background:
        radial-gradient(circle at top right, rgba(37, 99, 235, 0.07), transparent 30%),
        #ffffff;
    }

    .ns-dayend-alert-copy strong {
      color: #0f172a;
      display: block;
      font-size: 0.92rem;
      font-weight: 950;
    }

    .ns-dayend-alert-copy span {
      color: #64748b;
      display: block;
      font-size: 0.76rem;
      font-weight: 760;
      margin-top: 4px;
    }

    .ns-dayend-panel {
      margin-bottom: 12px;
    }

    .ns-dayend-panel-head {
      padding: 15px 17px;
      border-bottom: 1px solid rgba(15, 23, 42, 0.08);
      background:
        radial-gradient(circle at top right, rgba(37, 99, 235, 0.08), transparent 28%),
        #ffffff;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 14px;
      flex-wrap: wrap;
    }

    .ns-dayend-panel-head h3 {
      color: #001f5b;
      font-size: 1.05rem;
      line-height: 1.18;
      font-weight: 950;
      margin: 0;
    }

    .ns-dayend-panel-head p {
      color: #64748b;
      font-size: 0.76rem;
      line-height: 1.42;
      font-weight: 760;
      margin: 5px 0 0;
    }

    .ns-dayend-panel-body {
      padding: 14px;
      background: #f8fafc;
    }

    .ns-dayend-check-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 10px;
    }

    .ns-dayend-check-card {
      border-radius: 20px;
      background: #ffffff;
      border: 1px solid rgba(15, 23, 42, 0.08);
      border-top: 4px solid var(--tone, #2563eb);
      box-shadow: 0 12px 30px rgba(15, 23, 42, 0.055);
      padding: 14px;
      display: grid;
      gap: 12px;
      min-height: 170px;
    }

    .ns-dayend-check-top {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 10px;
    }

    .ns-dayend-check-icon {
      width: 42px;
      height: 42px;
      border-radius: 16px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: #eff6ff;
      color: var(--tone, #2563eb);
      font-size: 1.05rem;
      flex: 0 0 auto;
    }

    .ns-dayend-check-value {
      color: #0f172a;
      font-size: 1.65rem;
      line-height: 1;
      font-weight: 950;
      text-align: right;
    }

    .ns-dayend-check-card h4 {
      color: #0f172a;
      font-size: 0.94rem;
      line-height: 1.2;
      font-weight: 950;
      margin: 0;
    }

    .ns-dayend-check-card p {
      color: #64748b;
      font-size: 0.76rem;
      line-height: 1.42;
      font-weight: 760;
      margin: 4px 0 0;
    }

    .ns-dayend-card-action {
      margin-top: auto;
      min-height: 34px;
      border: 0;
      border-radius: 12px;
      background: #eff6ff;
      color: #1d4ed8;
      font-size: 0.76rem;
      font-weight: 950;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 7px;
      cursor: pointer;
    }

    .ns-dayend-fin-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
      gap: 10px;
    }

    .ns-dayend-fin-card {
      border-radius: 20px;
      border-top: 4px solid var(--tone, #2563eb);
      padding: 14px;
      min-height: 126px;
      display: flex;
      flex-direction: column;
      gap: 12px;
      justify-content: space-between;
      background: #ffffff;
    }

    .ns-dayend-fin-top {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
    }

    .ns-dayend-fin-icon {
      width: 42px;
      height: 42px;
      border-radius: 16px;
      background: #eff6ff;
      color: var(--tone, #2563eb);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 1.15rem;
    }

    .ns-dayend-fin-card span {
      color: #64748b;
      display: block;
      font-size: 0.68rem;
      font-weight: 950;
      text-transform: uppercase;
      letter-spacing: 0.06em;
    }

    .ns-dayend-fin-card strong {
      color: #0f172a;
      display: block;
      font-size: 1.18rem;
      line-height: 1.14;
      font-weight: 950;
      overflow-wrap: anywhere;
    }

    .ns-dayend-fin-card.is-emphasis strong {
      font-size: 1.34rem;
    }

    .ns-dayend-footer-note {
      margin-top: 12px;
      border-radius: 18px;
      background: #ffffff;
      border: 1px solid rgba(15, 23, 42, 0.08);
      padding: 12px 14px;
      color: #64748b;
      font-size: 0.78rem;
      font-weight: 780;
      display: flex;
      align-items: flex-start;
      gap: 9px;
    }

    .ns-dayend-swal {
      border-radius: 22px !important;
    }

    .ns-dayend-confirm ul {
      padding-left: 20px;
      margin: 10px 0;
    }

    @media (max-width: 1180px) {
      .ns-dayend-status-strip {
        grid-template-columns: 1fr;
      }

      .ns-dayend-check-grid {
        grid-template-columns: 1fr;
      }
    }

    @media (max-width: 720px) {
      .ns-dayend-page {
        padding-left: 8px;
        padding-right: 8px;
      }

      .ns-dayend-hero-actions {
        width: 100%;
        justify-content: flex-start;
      }

      .ns-dayend-date-picker,
      .ns-dayend-btn {
        width: 100%;
      }

      .ns-dayend-readiness-card {
        grid-template-columns: 1fr;
        justify-items: start;
      }
    }
  `;

  return (
    <div className="page-wrapper ns-dayend-page-wrapper">
      <style>{dashboardStyles}</style>

      {loading ? (
        <div className="ns-dayend-loader">
          <div className="ns-dayend-loader-card">
            <div className="ns-dayend-dots">
              <div className="ns-dayend-dot" />
              <div className="ns-dayend-dot" />
              <div className="ns-dayend-dot" />
              <div className="ns-dayend-dot" />
              <div className="ns-dayend-dot" />
            </div>
            <strong>Preparing day-end dashboard...</strong>
            <p style={{ marginTop: 6, marginBottom: 0 }}>
              Checking pending KOTs, unsettled bills, reservations, and financial totals.
            </p>
          </div>
        </div>
      ) : (
        <div className="content container-fluid ns-dayend-page">
          <section className="ns-dayend-hero">
            <div className="ns-dayend-hero-left">
              <span className="ns-dayend-hero-icon">
                <i className="fas fa-moon" />
              </span>

              <div>
                <div className="ns-dayend-eyebrow">POS Day End Control</div>
                <h2>Day End Dashboard</h2>
                <p>
                  Review open operational items, verify collections, and safely close the POS business date.
                </p>
              </div>
            </div>

            <div className="ns-dayend-hero-actions">
  

              <Tooltip title="Refresh current day-end summary">
                <Button
                  className="ns-dayend-btn ns-dayend-soft"
                  icon={<ReloadOutlined />}
                  onClick={fetchDashboardData}
                >
                  Refresh
                </Button>
              </Tooltip>

              <Button
                type="primary"
                className="ns-dayend-btn ns-dayend-primary"
                icon={<PlayCircleOutlined />}
                onClick={handleRunShiftEnd}
                disabled={isLoading}
              >
                Run Day End
              </Button>
            </div>
          </section>

          <section className="ns-dayend-status-strip">
            <div className="ns-dayend-readiness-card">
              <Progress
                type="dashboard"
                percent={readinessPercent}
                strokeColor={isFullyReady ? "#16a34a" : "#2563eb"}
                trailColor="#e2e8f0"
                format={(percent) => (
                  <span style={{ fontSize: 15, fontWeight: 950, color: "#0f172a" }}>
                    {percent}%
                  </span>
                )}
              />

              <div className="ns-dayend-readiness-copy">
                <span>Readiness</span>
                <strong>
                  {isFullyReady
                    ? "Ready to close"
                    : `${pendingItemCount} item${pendingItemCount === 1 ? "" : "s"} need attention`}
                </strong>
                <p>
                  Business date: <b>{selectedDate.format("DD MMM YYYY")}</b>. Resolve critical items before processing day end.
                </p>
              </div>
            </div>

            <div className="ns-dayend-alert-card">
              <div className="ns-dayend-alert-copy">
                <strong>Operational checklist</strong>
                <span>
                  The dashboard highlights open kitchen orders, bills, no-shows, and key cash movement before closure.
                </span>
              </div>

              <ShiftEndAlert />
            </div>
          </section>

          <section className="ns-dayend-panel">
            <div className="ns-dayend-panel-head">
              <div>
                <div className="ns-dayend-eyebrow">Readiness Checklist</div>
                <h3>Items to review before day end</h3>
                <p>Click into each item to complete operational closure before processing.</p>
              </div>

              {isFullyReady ? (
                <span className="ns-dayend-alert-copy" style={{ textAlign: "right" }}>
                  <strong style={{ color: "#047857" }}>
                    <CheckCircleOutlined /> Clear
                  </strong>
                  <span>No pending operational blockers found.</span>
                </span>
              ) : (
                <span className="ns-dayend-alert-copy" style={{ textAlign: "right" }}>
                  <strong style={{ color: "#b45309" }}>
                    <WarningOutlined /> Review needed
                  </strong>
                  <span>
                    {pendingItemCount} pending item{pendingItemCount === 1 ? "" : "s"} detected.
                  </span>
                </span>
              )}
            </div>

            <div className="ns-dayend-panel-body">
              <div className="ns-dayend-check-grid">
                {pendingCards.map((card) => (
                  <article className="ns-dayend-check-card" style={{ "--tone": card.tone }} key={card.key}>
                    <div className="ns-dayend-check-top">
                      <span className="ns-dayend-check-icon">
                        <i className={`fas ${card.icon}`} />
                      </span>
                      <span className="ns-dayend-check-value">{card.value}</span>
                    </div>

                    <div>
                      <h4>{card.label}</h4>
                      <p>{card.description}</p>
                    </div>

                    <button
                      type="button"
                      className="ns-dayend-card-action"
                      onClick={() => handleNavigation(card.key)}
                    >
                      View Details <ArrowRightOutlined />
                    </button>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section className="ns-dayend-panel">
            <div className="ns-dayend-panel-head">
              <div>
                <div className="ns-dayend-eyebrow">Financial Summary</div>
                <h3>Collections and sales position</h3>
                <p>Use these totals to verify cash, UPI, counter sales, room credit, and expense movement.</p>
              </div>
            </div>

            <div className="ns-dayend-panel-body">
              <div className="ns-dayend-fin-grid">
                {financialCards.map((card) => (
                  <article
                    className={`ns-dayend-fin-card ${card.emphasis ? "is-emphasis" : ""}`}
                    style={{ "--tone": card.tone }}
                    key={card.label}
                  >
                    <div className="ns-dayend-fin-top">
                      <span>{card.label}</span>
                      <span className="ns-dayend-fin-icon">
                        <i className={`fas ${card.icon}`} />
                      </span>
                    </div>

                    <strong>{formatMoney(currency, card.value)}</strong>
                  </article>
                ))}
              </div>

              <div className="ns-dayend-footer-note">
                <WarningOutlined style={{ color: "#f59e0b", marginTop: 2 }} />
                <span>
                  Day end should be processed only after operational items are reviewed and financial totals are verified.
                  On successful day end, the new date is saved in <b>posbusiness_date</b>.
                </span>
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default ShiftEndDashboard;
